import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, CalendarIcon, EyeIcon, MessageSquareIcon, DownloadIcon, XIcon, FileTextIcon, BuildingIcon, CheckIcon, BriefcaseIcon, AwardIcon, DollarSignIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface MyScholarshipsProps {
  isTabbed?: boolean;
}
// Sample scholarship application data
const scholarshipData = [{
  id: 'SCH001',
  name: 'ADNOC Excellence Scholarship',
  provider: 'ADNOC Group',
  program: 'Master of Petroleum Engineering',
  university: 'Stanford University',
  location: 'California, USA',
  status: 'under_review',
  submissionDate: '2023-05-12',
  lastUpdated: '2023-05-18',
  decisionDate: '2023-06-30',
  priority: 'high',
  progress: 100,
  amount: '$50,000 per year',
  duration: '2 years',
  notes: 'Your scholarship application is currently being reviewed by the committee.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'SCH002',
  name: 'Etisalat Digital Innovation Scholarship',
  provider: 'Etisalat',
  program: 'PhD in Computer Science',
  university: 'MIT',
  location: 'Massachusetts, USA',
  status: 'approved',
  submissionDate: '2023-04-05',
  lastUpdated: '2023-05-01',
  decisionDate: '2023-05-01',
  priority: 'high',
  progress: 100,
  amount: '$60,000 per year',
  duration: '4 years',
  notes: 'Congratulations! Your scholarship application has been approved. Please check your email for further instructions.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Research Proposal',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'SCH003',
  name: 'Emirates Foundation Scholarship',
  provider: 'Emirates Foundation',
  program: 'Master of Public Health',
  university: 'Harvard University',
  location: 'Massachusetts, USA',
  status: 'rejected',
  submissionDate: '2023-03-15',
  lastUpdated: '2023-04-10',
  decisionDate: '2023-04-10',
  priority: 'medium',
  progress: 100,
  amount: '$45,000 per year',
  duration: '2 years',
  notes: 'We regret to inform you that your scholarship application was not successful. You may apply again in the next cycle.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'SCH004',
  name: 'Dubai Future Foundation Scholarship',
  provider: 'Dubai Future Foundation',
  program: 'Master of Artificial Intelligence',
  university: 'University of Oxford',
  location: 'Oxford, United Kingdom',
  status: 'submitted',
  submissionDate: '2023-05-05',
  lastUpdated: '2023-05-05',
  decisionDate: null,
  priority: 'medium',
  progress: 100,
  amount: '£40,000 per year',
  duration: '1 year',
  notes: 'Your scholarship application has been submitted and is pending initial review.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'SCH005',
  name: 'Mubadala Leadership Scholarship',
  provider: 'Mubadala Investment Company',
  program: 'MBA',
  university: 'INSEAD',
  location: 'Fontainebleau, France',
  status: 'in_progress',
  submissionDate: null,
  lastUpdated: '2023-05-18',
  decisionDate: null,
  priority: 'high',
  progress: 60,
  amount: '€70,000 total',
  duration: '1 year',
  notes: 'Please complete your statement of purpose and upload your recommendation letters to proceed with your scholarship application.',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Statement of Purpose',
    status: 'missing'
  }, {
    name: 'Recommendation Letters',
    status: 'pending'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}];
const MyScholarships: React.FC<MyScholarshipsProps> = ({
  isTabbed = false
}) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Filter scholarships based on status and search query
  const filteredScholarships = scholarshipData.filter(scholarship => {
    const matchesStatus = statusFilter === 'all' || scholarship.status === statusFilter;
    const matchesSearch = searchQuery === '' || scholarship.name.toLowerCase().includes(searchQuery.toLowerCase()) || scholarship.provider.toLowerCase().includes(searchQuery.toLowerCase()) || scholarship.program.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  // Format date string
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Get status display information
  const getStatusInfo = status => {
    switch (status) {
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-4515" />
        };
      case 'submitted':
        return {
          label: 'Submitted',
          color: 'bg-gray-100 text-gray-700',
          icon: <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-4516" />
        };
      case 'under_review':
        return {
          label: 'Under Review',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-4517" />
        };
      case 'approved':
        return {
          label: 'Approved',
          color: 'bg-green-100 text-green-700',
          icon: <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-4518" />
        };
      case 'rejected':
        return {
          label: 'Rejected',
          color: 'bg-red-100 text-red-700',
          icon: <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-4519" />
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-700',
          icon: <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-4520" />
        };
    }
  };
  // Get priority badge style
  const getPriorityBadge = priority => {
    return 'bg-gray-100 text-gray-700 border border-gray-200';
  };
  // Get action button based on status
  const getActionButton = scholarship => {
    switch (scholarship.status) {
      case 'in_progress':
        return {
          label: 'Continue Application',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: () => navigate(`/student/scholarship-application/${scholarship.id}`)
        };
      case 'submitted':
      case 'under_review':
        return {
          label: 'Track Status',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: () => handleViewDetails(scholarship)
        };
      case 'approved':
        return {
          label: 'View Award Details',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: () => handleViewDetails(scholarship)
        };
      case 'rejected':
        return {
          label: 'View Feedback',
          color: 'bg-gray-700 hover:bg-gray-800 text-white',
          action: () => handleViewDetails(scholarship)
        };
      default:
        return {
          label: 'View Details',
          color: 'bg-primary-500 hover:bg-primary-600 text-white',
          action: () => handleViewDetails(scholarship)
        };
    }
  };
  // Handle view details
  const handleViewDetails = scholarship => {
    setSelectedScholarship(scholarship);
    setShowDetailModal(true);
  };
  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedScholarship(null);
  };
  const content = <div className="space-y-6" data-id="element-4521">
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-4522">
        <div className="flex flex-col md:flex-row gap-4 justify-between" data-id="element-4523">
          <div className="relative w-full md:w-64" data-id="element-4524">
            <input type="text" placeholder="Search scholarship applications..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4525" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-4526" />
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-4527">
            <div className="flex items-center space-x-2" data-id="element-4528">
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('all')} data-id="element-4529">
                All
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'in_progress' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('in_progress')} data-id="element-4530">
                In Progress
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'submitted' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('submitted')} data-id="element-4531">
                Submitted
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'under_review' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('under_review')} data-id="element-4532">
                Under Review
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'approved' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('approved')} data-id="element-4533">
                Approved
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('rejected')} data-id="element-4534">
                Rejected
              </button>
            </div>
            <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-4535">
              <FilterIcon className="h-4 w-4 mr-1.5" data-id="element-4536" />
              More Filters
            </button>
          </div>
        </div>
      </div>
      {/* Scholarships List */}
      {filteredScholarships.length > 0 ? <div className="space-y-4" data-id="element-4537">
          {filteredScholarships.map(scholarship => {
        const statusInfo = getStatusInfo(scholarship.status);
        const actionButton = getActionButton(scholarship);
        return <div key={scholarship.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 transition-all" data-id="element-4538">
                <div className="p-5" data-id="element-4539">
                  <div className="flex flex-col md:flex-row justify-between md:items-start gap-4" data-id="element-4540">
                    {/* Scholarship Information */}
                    <div className="flex-grow" data-id="element-4541">
                      <div className="flex justify-between items-start" data-id="element-4542">
                        <div data-id="element-4543">
                          <h3 className="text-lg font-medium text-gray-800" data-id="element-4544">
                            {scholarship.name}
                          </h3>
                          <div className="flex items-center mt-1" data-id="element-4545">
                            <BriefcaseIcon className="w-4 h-4 text-gray-500 mr-1" data-id="element-4546" />
                            <p className="text-sm text-gray-600" data-id="element-4547">
                              {scholarship.provider}
                            </p>
                          </div>
                          <div className="flex items-center mt-1" data-id="element-4548">
                            <BuildingIcon className="w-4 h-4 text-gray-500 mr-1" data-id="element-4549" />
                            <p className="text-sm text-gray-600" data-id="element-4550">
                              {scholarship.program} at {scholarship.university}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0" data-id="element-4551">
                          <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${statusInfo.color}`} data-id="element-4552">
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      {/* Scholarship Details */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-id="element-4553">
                        <div data-id="element-4554">
                          <p className="text-xs text-gray-500" data-id="element-4555">Amount</p>
                          <p className="text-gray-700" data-id="element-4556">{scholarship.amount}</p>
                        </div>
                        <div data-id="element-4557">
                          <p className="text-xs text-gray-500" data-id="element-4558">Duration</p>
                          <p className="text-gray-700" data-id="element-4559">
                            {scholarship.duration}
                          </p>
                        </div>
                        {scholarship.submissionDate && <div data-id="element-4560">
                            <p className="text-xs text-gray-500" data-id="element-4561">
                              Submitted On
                            </p>
                            <p className="text-gray-700" data-id="element-4562">
                              {formatDate(scholarship.submissionDate)}
                            </p>
                          </div>}
                      </div>
                      {/* Progress bar for in-progress applications */}
                      {scholarship.status === 'in_progress' && <div className="mt-4" data-id="element-4563">
                          <div className="flex items-center justify-between mb-1" data-id="element-4564">
                            <p className="text-sm text-gray-600" data-id="element-4565">
                              Application Progress
                            </p>
                            <p className="text-sm font-medium text-gray-700" data-id="element-4566">
                              {scholarship.progress}%
                            </p>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-4567">
                            <div className="bg-primary-500 h-2.5 rounded-full" style={{
                      width: `${scholarship.progress}%`
                    }} data-id="element-4568"></div>
                          </div>
                          <p className="mt-2 text-sm text-gray-600" data-id="element-4569">
                            {scholarship.notes}
                          </p>
                        </div>}
                      {/* Badges */}
                      <div className="mt-4 flex flex-wrap gap-2" data-id="element-4570">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${getPriorityBadge(scholarship.priority)}`} data-id="element-4571">
                          {scholarship.priority.charAt(0).toUpperCase() + scholarship.priority.slice(1)}{' '}
                          Priority
                        </span>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 md:items-end" data-id="element-4572">
                      <button onClick={actionButton.action} className={`px-4 py-2 rounded-md text-sm font-medium ${actionButton.color}`} data-id="element-4573">
                        {actionButton.label}
                      </button>
                      <button onClick={() => handleViewDetails(scholarship)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-4574">
                        <EyeIcon className="w-4 h-4 mr-1.5" data-id="element-4575" />
                        View Details
                      </button>
                      {/* Additional actions for submitted scholarships */}
                      {(scholarship.status === 'submitted' || scholarship.status === 'under_review') && <div className="flex gap-2 mt-2" data-id="element-4576">
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-4577">
                            <MessageSquareIcon className="w-4 h-4" data-id="element-4578" />
                          </button>
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-4579">
                            <DownloadIcon className="w-4 h-4" data-id="element-4580" />
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>;
      })}
        </div> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center" data-id="element-4581">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-4582">
            <AwardIcon className="w-8 h-8 text-gray-400" data-id="element-4583" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2" data-id="element-4584">
            No scholarship applications found
          </h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto mb-5" data-id="element-4585">
            {searchQuery ? 'No scholarship applications match your search criteria. Try adjusting your filters or search terms.' : "You haven't applied for any scholarships yet. Explore available scholarships and apply to ones that match your qualifications."}
          </p>
          <button onClick={() => navigate('/student/scholarships')} className="h-10 px-4 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center" data-id="element-4586">
            <AwardIcon className="w-4 h-4 mr-2" data-id="element-4587" />
            Explore Scholarships
          </button>
        </div>}
      {/* Scholarship Details Modal */}
      {showDetailModal && selectedScholarship && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4588">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-4589">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal} data-id="element-4590"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-4591">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-4592">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-4593">
                <div className="flex justify-between items-start mb-4" data-id="element-4594">
                  <div data-id="element-4595">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4596">
                      Scholarship Application Details
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-4597">
                      {selectedScholarship.name} for{' '}
                      {selectedScholarship.program}
                    </p>
                  </div>
                  <button onClick={closeDetailModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-4598">
                    <XIcon className="h-6 w-6" data-id="element-4599" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4600">
                  {/* Scholarship Information */}
                  <div data-id="element-4601">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4602">
                      Application Information
                    </h4>
                    <div className="space-y-3" data-id="element-4603">
                      <div data-id="element-4604">
                        <span className="text-sm text-gray-500" data-id="element-4605">
                          Application ID:
                        </span>
                        <p className="text-sm text-gray-900 font-medium" data-id="element-4606">
                          {selectedScholarship.id}
                        </p>
                      </div>
                      <div data-id="element-4607">
                        <span className="text-sm text-gray-500" data-id="element-4608">Status:</span>
                        <p className="text-sm" data-id="element-4609">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusInfo(selectedScholarship.status).color}`} data-id="element-4610">
                            {getStatusInfo(selectedScholarship.status).label}
                          </span>
                        </p>
                      </div>
                      <div data-id="element-4611">
                        <span className="text-sm text-gray-500" data-id="element-4612">Priority:</span>
                        <p className="text-sm" data-id="element-4613">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getPriorityBadge(selectedScholarship.priority)}`} data-id="element-4614">
                            {selectedScholarship.priority.charAt(0).toUpperCase() + selectedScholarship.priority.slice(1)}{' '}
                            Priority
                          </span>
                        </p>
                      </div>
                      {selectedScholarship.submissionDate && <div data-id="element-4615">
                          <span className="text-sm text-gray-500" data-id="element-4616">
                            Submitted On:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-4617">
                            {formatDate(selectedScholarship.submissionDate)}
                          </p>
                        </div>}
                      <div data-id="element-4618">
                        <span className="text-sm text-gray-500" data-id="element-4619">
                          Last Updated:
                        </span>
                        <p className="text-sm text-gray-900" data-id="element-4620">
                          {formatDate(selectedScholarship.lastUpdated)}
                        </p>
                      </div>
                      {selectedScholarship.decisionDate && <div data-id="element-4621">
                          <span className="text-sm text-gray-500" data-id="element-4622">
                            Decision Date:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-4623">
                            {formatDate(selectedScholarship.decisionDate)}
                          </p>
                        </div>}
                    </div>
                  </div>
                  {/* Scholarship Details */}
                  <div data-id="element-4624">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4625">
                      Scholarship Details
                    </h4>
                    <div className="space-y-3" data-id="element-4626">
                      <div data-id="element-4627">
                        <span className="text-sm text-gray-500" data-id="element-4628">Provider:</span>
                        <p className="text-sm text-gray-900" data-id="element-4629">
                          {selectedScholarship.provider}
                        </p>
                      </div>
                      <div data-id="element-4630">
                        <span className="text-sm text-gray-500" data-id="element-4631">Program:</span>
                        <p className="text-sm text-gray-900" data-id="element-4632">
                          {selectedScholarship.program}
                        </p>
                      </div>
                      <div data-id="element-4633">
                        <span className="text-sm text-gray-500" data-id="element-4634">
                          University:
                        </span>
                        <p className="text-sm text-gray-900" data-id="element-4635">
                          {selectedScholarship.university}
                        </p>
                      </div>
                      <div data-id="element-4636">
                        <span className="text-sm text-gray-500" data-id="element-4637">Location:</span>
                        <p className="text-sm text-gray-900" data-id="element-4638">
                          {selectedScholarship.location}
                        </p>
                      </div>
                      <div data-id="element-4639">
                        <span className="text-sm text-gray-500" data-id="element-4640">Amount:</span>
                        <p className="text-sm text-gray-900" data-id="element-4641">
                          {selectedScholarship.amount}
                        </p>
                      </div>
                      <div data-id="element-4642">
                        <span className="text-sm text-gray-500" data-id="element-4643">Duration:</span>
                        <p className="text-sm text-gray-900" data-id="element-4644">
                          {selectedScholarship.duration}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Documents */}
                <div className="mt-6" data-id="element-4645">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4646">
                    Supporting Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-4647">
                    {selectedScholarship.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-id="element-4648">
                        <div className="flex items-center" data-id="element-4649">
                          <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4650" />
                          <span className="text-sm text-gray-700" data-id="element-4651">
                            {doc.name}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${doc.status === 'verified' ? 'bg-gray-100 text-gray-700' : doc.status === 'pending' ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-4652">
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                      </div>)}
                  </div>
                </div>
                {/* Notes */}
                <div className="mt-6" data-id="element-4653">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4654">
                    Notes
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3" data-id="element-4655">
                    <p className="text-sm text-gray-700" data-id="element-4656">
                      {selectedScholarship.notes}
                    </p>
                  </div>
                </div>
                {/* Timeline */}
                <div className="mt-6" data-id="element-4657">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-4658">
                    Application Timeline
                  </h4>
                  <div className="space-y-6" data-id="element-4659">
                    <div className="relative" data-id="element-4660">
                      <div className="flex items-center" data-id="element-4661">
                        <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4662">
                          <CalendarIcon className="h-5 w-5 text-gray-600" data-id="element-4663" />
                        </div>
                        <div className="ml-4" data-id="element-4664">
                          <h3 className="text-sm font-medium text-gray-900" data-id="element-4665">
                            Application Started
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-4666">
                            {formatDate(new Date(new Date(selectedScholarship.lastUpdated).getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4667"></div>
                    </div>
                    {selectedScholarship.submissionDate && <div className="relative" data-id="element-4668">
                        <div className="flex items-center" data-id="element-4669">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4670">
                            <CheckIcon className="h-5 w-5 text-gray-600" data-id="element-4671" />
                          </div>
                          <div className="ml-4" data-id="element-4672">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4673">
                              Application Submitted
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4674">
                              {formatDate(selectedScholarship.submissionDate)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4675"></div>
                      </div>}
                    {selectedScholarship.status === 'under_review' && <div className="relative" data-id="element-4676">
                        <div className="flex items-center" data-id="element-4677">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-4678">
                            <ClockIcon className="h-5 w-5 text-gray-600" data-id="element-4679" />
                          </div>
                          <div className="ml-4" data-id="element-4680">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4681">
                              Under Review
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4682">
                              {formatDate(selectedScholarship.lastUpdated)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-4683"></div>
                      </div>}
                    {(selectedScholarship.status === 'approved' || selectedScholarship.status === 'rejected') && <div className="relative" data-id="element-4684">
                        <div className="flex items-center" data-id="element-4685">
                          <div className={`h-9 w-9 rounded-full ${selectedScholarship.status === 'approved' ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center z-10`} data-id="element-4686">
                            {selectedScholarship.status === 'approved' ? <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-4687" /> : <XCircleIcon className="h-5 w-5 text-red-600" data-id="element-4688" />}
                          </div>
                          <div className="ml-4" data-id="element-4689">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-4690">
                              Application{' '}
                              {selectedScholarship.status === 'approved' ? 'Approved' : 'Rejected'}
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-4691">
                              {formatDate(selectedScholarship.decisionDate)}
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-4692">
                <button onClick={getActionButton(selectedScholarship).action} className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${getActionButton(selectedScholarship).color}`} data-id="element-4693">
                  {getActionButton(selectedScholarship).label}
                </button>
                <button onClick={closeDetailModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-4694">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
  return <>
      {!isTabbed ? <Layout title="My Scholarship Applications" persona="Student" data-id="element-4695">
          {content}
        </Layout> : content}
    </>;
};
export default MyScholarships;